import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Organisation, PlanRun, TaskPlan, User } from "../types";
import { formatDuration } from "../shared/scheduling";
import { formatRunTime } from "../shared/date";
import {
  acceptInvitation,
  declineInvitation,
} from "../services/storageService";
import { useNotifications } from "./NotificationsProvider";
import { useWorkspace } from "./WorkspaceProvider";
import { ConfirmationModal, ErrorModal } from "./Modals";
import {
  CheckCircle,
  ChevronRight,
  Clock,
  Filter,
  History,
  Layers,
  Play,
  Plus,
  Search,
  Share2,
  Trash2,
  Users,
  XCircle,
  Zap,
} from "./Icons";

interface DashboardProps {
  plans: TaskPlan[];
  runs: PlanRun[];
  currentUser: User;
  currentOrg: Organisation;
  isLoading?: boolean;
  onCreatePlan: () => void;
  onEditPlan: (id: string) => void;
  onDeletePlan: (e: React.MouseEvent, id: string) => void;
  onDeleteRun: (id: string) => void;
  onRunPlan: (e: React.MouseEvent, plan: TaskPlan) => void;
  onSharePlan: (e: React.MouseEvent, plan: TaskPlan) => void;
  onResumeRun: (runId: string) => void;
  onRefresh?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({
  plans,
  runs,
  currentUser,
  currentOrg,
  isLoading = false,
  onCreatePlan,
  onEditPlan,
  onDeletePlan,
  onDeleteRun,
  onRunPlan,
  onSharePlan,
  onResumeRun,
  onRefresh,
}) => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications } = useNotifications();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "alpha">("newest");
  const [deletePlanId, setDeletePlanId] = useState<string | null>(null);
  const [deleteRunId, setDeleteRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState<string | null>(null);

  const { refreshOrgs } = useWorkspace();
  const invitations = notifications.invitations;
  const isLoadingInvitations = false; // Handled by NotificationsProvider initial load or WS

  const isMaintainer = currentOrg.role === "MAINTAINER";
  const isShared = currentOrg.type === "SHARED";
  const isTrialExpired =
    currentOrg.subscriptionStatus === "TRIALING" &&
    (currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now();

  const isActive =
    currentOrg.subscriptionStatus === "ACTIVE" ||
    (currentOrg.subscriptionStatus === "TRIALING" && !isTrialExpired);
  const isReadOnly = isShared && !isActive;

  useEffect(() => {
    if (location.state?.welcome) {
      setWelcomeMessage(
        `Welcome to ${currentOrg.name}! You have successfully joined the workspace.`,
      );
      // Clear state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, currentOrg.name]);

  const handleAcceptInvite = async (id: string) => {
    try {
      const inviteToAccept = invitations.find((i) => i.id === id);
      const token = await getToken();
      await acceptInvitation(id, token || undefined);
      await refreshOrgs();

      if (inviteToAccept && inviteToAccept.organisationSlug) {
        navigate(`/${inviteToAccept.organisationSlug}/dashboard`, {
          state: { welcome: true },
        });
      } else {
        if (onRefresh) onRefresh();
      }
    } catch (e) {
      console.error(e);
      setError("Failed to accept invitation. Please try again.");
    }
  };

  const handleDeclineInvite = async (id: string) => {
    try {
      const token = await getToken();
      await declineInvitation(id, token || undefined);
    } catch (e) {
      console.error(e);
      setError("Failed to decline invitation. Please try again.");
    }
  };

  const filteredPlans = useMemo(() => {
    let result = plans.filter(
      (p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    switch (sortBy) {
      case "newest":
        result.sort((a, b) => b.createdAt - a.createdAt);
        break;
      case "oldest":
        result.sort((a, b) => a.createdAt - b.createdAt);
        break;
      case "alpha":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }
    return result;
  }, [plans, searchTerm, sortBy]);

  const recentRuns = useMemo(() => {
    return [...runs].sort((a, b) => b.startTime - a.startTime).slice(0, 5);
  }, [runs]);

  if (isLoading || (isLoadingInvitations && plans.length === 0)) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 dark:bg-slate-800 rounded mb-2"></div>
        <div className="h-4 w-64 bg-slate-200 dark:bg-slate-800 rounded mb-8"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-48 bg-slate-200 dark:bg-slate-800 rounded-xl"
              ></div>
            ))}
          </div>
          <div className="space-y-4">
            <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ErrorModal
        isOpen={!!error}
        onClose={() => setError(null)}
        message={error || ""}
      />
      {welcomeMessage && (
        <div className="mb-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3 text-green-800 dark:text-green-200 shadow-sm animate-in fade-in slide-in-from-top-2">
          <CheckCircle size={20} className="flex-shrink-0" />
          <p className="font-medium">{welcomeMessage}</p>
          <button
            onClick={() => setWelcomeMessage(null)}
            className="ml-auto text-sm hover:underline opacity-70 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {currentOrg.subscriptionStatus === "TRIALING" && (
        <div
          className={`mb-8 border rounded-lg p-4 flex items-center gap-4 shadow-sm animate-in fade-in slide-in-from-top-2 relative overflow-hidden group ${
            (currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now()
              ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
              : "bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-900 text-indigo-700 dark:text-indigo-300"
          }`}
        >
          <div
            className={`p-2 rounded-full ${(currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now() ? "bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400" : "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400"}`}
          >
            <Zap size={18} />
          </div>
          <div>
            <h3 className="font-bold text-sm">
              {(currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now()
                ? "Trial Expired"
                : "Trial Active"}
            </h3>
            <p className="text-sm opacity-90">
              {(currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now()
                ? "Your trial has ended. Upgrade to a paid plan to restore full access."
                : `You have ${Math.max(0, Math.ceil(((currentOrg.subscriptionCurrentPeriodEnd || 0) - Date.now()) / (1000 * 60 * 60 * 24)))} days remaining in your trial.`}
            </p>
          </div>
          {(currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now() ? (
            <button
              onClick={() => navigate(`/${currentOrg.slug}/workspace/billing`)}
              className="ml-auto px-4 py-1.5 bg-red-600 text-white rounded-md text-xs font-bold hover:bg-red-700 transition shadow-sm"
            >
              Upgrade Now
            </button>
          ) : (
            <button
              onClick={(e) => {
                const parent = e.currentTarget.parentElement;
                if (parent) parent.style.display = "none";
              }}
              className="ml-auto p-1 text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 rounded transition"
              aria-label="Dismiss"
            >
              <XCircle size={18} />
            </button>
          )}
        </div>
      )}

      {invitations.length > 0 && (
        <div className="mb-8 space-y-2">
          {invitations.map((invite) => (
            <div
              key={invite.id}
              className="bg-indigo-600 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between text-white shadow-lg gap-4"
            >
              <div className="flex items-center gap-3 w-full">
                <div className="bg-white/20 p-2 rounded-full flex-shrink-0">
                  <Users size={20} />
                </div>
                <div>
                  <p className="font-bold">
                    You have been invited to join{" "}
                    {invite.organisationName || "a workspace"}
                  </p>
                  <p className="text-indigo-100 text-sm">
                    Role:{" "}
                    <span className="font-mono bg-indigo-700 px-1 rounded">
                      {invite.role}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto justify-end">
                <button
                  onClick={() => handleDeclineInvite(invite.id)}
                  className="px-3 py-1.5 rounded bg-indigo-700 hover:bg-indigo-800 text-sm font-medium transition"
                >
                  Decline
                </button>
                <button
                  onClick={() => handleAcceptInvite(invite.id)}
                  className="px-3 py-1.5 rounded bg-white text-indigo-600 hover:bg-indigo-50 text-sm font-bold transition shadow-sm whitespace-nowrap"
                >
                  Join Workspace
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Bar */}
      <div className="flex flex-col md:flex-row justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Overview of your automation workflows.
          </p>
        </div>
        <div className="flex items-center gap-3 w-full lg:w-auto">
          <div className="relative flex-1 lg:flex-none lg:w-64">
            <Search
              className="absolute left-3 top-2.5 text-slate-400"
              size={16}
            />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search plans..."
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
            />
          </div>
          {isMaintainer && !isReadOnly && (
            <button
              onClick={onCreatePlan}
              data-testid="create-plan-button"
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-nowrap px-4 py-2 rounded-lg shadow-sm font-medium flex items-center gap-2 transition-all active:scale-95"
            >
              <Plus size={18} /> New Plan
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - Plans */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <Layers size={16} /> Active Plans
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="appearance-none pl-3 pr-8 py-1.5 bg-transparent text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-indigo-600 cursor-pointer focus:outline-none"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="alpha">Alphabetical</option>
              </select>
              <Filter
                size={12}
                className="absolute right-2 top-2.5 pointer-events-none text-slate-400"
              />
            </div>
          </div>

          {filteredPlans.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-12 text-center">
              <div className="w-16 h-16 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                <Search size={24} />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                No plans found
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                {searchTerm
                  ? `No plans match "${searchTerm}"`
                  : "Get started by creating your first workflow."}
              </p>
              {!searchTerm && isMaintainer && (
                <button
                  onClick={onCreatePlan}
                  className="mt-4 text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                >
                  Create new plan
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredPlans.map((plan) => (
                <div
                  key={plan.id}
                  data-testid="plan-card"
                  onClick={() => onEditPlan(plan.id)}
                  className="group bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-500 transition-all cursor-pointer flex flex-col h-full relative"
                >
                  {/* Action buttons visible if Maintainer or Owner or Editor */}
                  {(isMaintainer ||
                    plan.ownerId === currentUser.id ||
                    plan.userPermission === "EDITOR" ||
                    plan.userPermission === "OWNER") && (
                    <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSharePlan(e, plan);
                        }}
                        className="p-1.5 text-slate-400 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                        title="Share"
                      >
                        <Share2 size={14} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletePlanId(plan.id);
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}

                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <h3
                        data-testid="plan-card-title"
                        className="font-bold text-slate-800 dark:text-white truncate pr-16"
                      >
                        {plan.name}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 h-10">
                      {plan.description}
                    </p>
                  </div>

                  <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                    <div className="flex gap-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide">
                      <span className="flex items-center gap-1">
                        <Layers size={12} /> {plan.taskCount || 0} Steps
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />{" "}
                        {formatDuration(plan.totalDuration || 0)}
                      </span>
                    </div>
                    {/* Run button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRunPlan(e, plan);
                      }}
                      data-testid="run-plan-button"
                      className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-500 dark:hover:text-white flex items-center justify-center transition-colors shadow-sm"
                      title="Run Plan"
                    >
                      <Play size={14} fill="currentColor" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar - Recent Activity */}
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            <History size={16} /> Recent Runs
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            {recentRuns.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                  No run history yet.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {recentRuns.map((run) => (
                  <div
                    key={run.id}
                    onClick={() => onResumeRun(run.id)}
                    className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition cursor-pointer group relative pr-10"
                  >
                    {isMaintainer && (
                      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteRunId(run.id);
                          }}
                          data-testid="delete-run-button"
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Delete Run"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-slate-800 dark:text-white text-sm truncate pr-2">
                        {run.name}
                      </div>
                      <StatusBadge status={run.status} />
                    </div>
                    <div className="flex justify-between items-center text-xs text-slate-400 dark:text-slate-500">
                      {(() => {
                        const { relative, full } = formatRunTime(run.status, run.startTime, run.scheduledStartTime);
                        return (
                          <span title={full}>
                            {relative}
                          </span>
                        );
                      })()}
                      <span className="flex items-center gap-1 text-slate-400 group-hover:text-indigo-500 transition-colors">
                        View <ChevronRight size={12} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="p-3 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 text-center">
              <button
                onClick={() => navigate(`/${currentOrg.slug}/runs`)}
                className="text-xs font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              >
                View All History
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={!!deletePlanId}
        onClose={() => setDeletePlanId(null)}
        onConfirm={() => {
          if (deletePlanId) {
            const mockEvent = { stopPropagation: () => {} } as React.MouseEvent;
            onDeletePlan(mockEvent, deletePlanId);
          }
        }}
        title="Delete Plan"
        message="Are you sure you want to delete this task plan? This cannot be undone."
        confirmText="Delete Plan"
        confirmVariant="danger"
      />
      <ConfirmationModal
        isOpen={!!deleteRunId}
        onClose={() => setDeleteRunId(null)}
        onConfirm={() => {
          if (deleteRunId) {
            onDeleteRun(deleteRunId);
            setDeleteRunId(null);
          }
        }}
        title="Delete Run"
        message="Are you sure you want to delete this run history? This cannot be undone."
        confirmText="Delete Run"
        confirmVariant="danger"
      />
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  let classes = "";
  switch (status) {
    case "COMPLETED":
      classes =
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300";
      break;
    case "RUNNING":
      classes =
        "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 animate-pulse";
      break;
    case "SCHEDULED":
      classes =
        "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300";
      break;
    case "FAILED":
      classes = "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300";
      break;
    default:
      classes =
        "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400";
  }

  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${classes}`}
    >
      {status}
    </span>
  );
};

export default Dashboard;
